import axios from 'axios';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { getAccessToken } from '../hooks/auth-provider.tsx';
import type { AccountListModel } from './account.type.ts';
import { ConfirmationStatusEnum } from './confirmation-status.model.ts';
import { type CalendarEvent, EventTypeEnum } from './event.type.ts';
import { MeetingStatusTypeEnum } from './meeting.type.ts';
import type { StaffMemberModel, StaffRoleEnum, StaffRoleModel } from './staff-member.type.ts';
import { type UserModel, UserTypeEnum } from './user-type.ts';

export const emailPattern = /^[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export enum EventStatus {
  Available = 'Available',
  Requested = 'Requested',
  Approved = 'Approved',
  Canceled = 'Canceled',
  Reserved = 'Reserved',
  Booked = 'Booked',
}

export const eventBadgeColor = {
  [EventStatus.Available]: 'zinc' as const,
  [EventStatus.Requested]: 'amber' as const,
  [EventStatus.Approved]: 'green' as const,
  [EventStatus.Canceled]: 'red' as const,
  [EventStatus.Reserved]: 'sky' as const,
  [EventStatus.Booked]: 'black' as const,
};

export const eventDotColor = {
  [EventStatus.Available]: 'bg-gray-400',
  [EventStatus.Requested]: 'bg-amber-400',
  [EventStatus.Approved]: 'bg-secondary-400',
  [EventStatus.Canceled]: 'bg-red-400',
  [EventStatus.Reserved]: 'bg-primary-400',
  [EventStatus.Booked]: 'bg-black',
};

export const eventTextColor = {
  [EventStatus.Available]: 'text-gray-800',
  [EventStatus.Requested]: 'text-amber-800',
  [EventStatus.Approved]: 'text-secondary-800',
  [EventStatus.Canceled]: 'text-red-800',
  [EventStatus.Reserved]: 'text-primary-800',
  [EventStatus.Booked]: 'text-white',
};

export const eventBackgroundColor = {
  [EventStatus.Available]: 'bg-gray-200',
  [EventStatus.Requested]: 'bg-amber-200',
  [EventStatus.Approved]: 'bg-secondary-200',
  [EventStatus.Canceled]: 'bg-red-200',
  [EventStatus.Reserved]: 'bg-primary-200',
  [EventStatus.Booked]: 'bg-black',
};

export const eventHoverColor = {
  [EventStatus.Available]: 'hover:text-gray-600',
  [EventStatus.Requested]: 'hover:text-amber-600',
  [EventStatus.Approved]: 'hover:text-secondary-600',
  [EventStatus.Canceled]: 'hover:text-red-600',
  [EventStatus.Reserved]: 'hover:text-primary-600',
  [EventStatus.Booked]: '',
};

export const eventColors = new Proxy(
  {},
  {
    get(_target, property, _receiver) {
      return `${eventTextColor[property]} ${eventBackgroundColor[property]}`;
    },
  },
);

export const eventInteractionColors = new Proxy(
  {},
  {
    get(_target, property, _receiver) {
      return `${eventTextColor[property]} ${eventBackgroundColor[property]} ${eventHoverColor[property]}`;
    },
  },
);

export const getEventStatus = (event: CalendarEvent, user: UserModel | undefined) => {
  if (event.eventType === EventTypeEnum.AvailabilitySlot) {
    return eventReserved(event) ? EventStatus.Reserved : EventStatus.Available;
  }

  if (
    user?.userType === UserTypeEnum.DrugRep &&
    event.representative?.userId !== user?.id &&
    [MeetingStatusTypeEnum.Requested, MeetingStatusTypeEnum.Approved].includes(event.statusId)
  ) {
    return EventStatus.Booked;
  }

  switch (event.statusId) {
    case MeetingStatusTypeEnum.Approved:
      return EventStatus.Approved;
    case MeetingStatusTypeEnum.Canceled:
      return EventStatus.Canceled;
    case MeetingStatusTypeEnum.Requested:
      return EventStatus.Requested;
    default:
      return EventStatus.Available;
  }
};

export const getAvailabilityColor = (confirmationStatus: ConfirmationStatusEnum) => {
  switch (confirmationStatus) {
    case ConfirmationStatusEnum.Available:
      return 'success';
    case ConfirmationStatusEnum.Unavailable:
      return 'danger';
    default:
      return 'default';
  }
};

export const getTimeFormat = (time: any) => {
  return formatDate(time, DateTime.TIME_24_SIMPLE, time);
};

export const getDateFormat = (date: any) => {
  return formatDate(date, DateTime.DATE_SHORT, date);
};

export const downloadFile = (value: string) => {
  axios
    .get(`${import.meta.env.VITE_BASE_API_URL}/api/File/Download/${value}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
    .then(response => {
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${value.substring(value.lastIndexOf('/') + 1)}`);
      document.body.appendChild(link);
      link.click();
    });
};

export const isFuture = (date: string) => {
  return DateTime.fromISO(date).diffNow('hours').hours > 1;
};

export const hasPermission = (
  user: StaffMemberModel,
  staffRoleId: StaffRoleEnum,
  accountId?: number,
) => {
  return (user as StaffMemberModel)?.accountSiteStaffMembers?.some(
    x => x.staffRole.id >= staffRoleId && (accountId ? accountId === x.accountId : true),
  );
};

export const hasAnyPermission = (
  user: StaffMemberModel,
  staffRoleId: StaffRoleEnum,
  accountIds: number[],
) => {
  return (user as StaffMemberModel)?.accountSiteStaffMembers?.some(
    x => x.staffRole.id >= staffRoleId && accountIds.includes(x.accountId),
  );
};

export const filterRoles = (
  data: StaffRoleModel[],
  user: UserModel,
  currentAccount: AccountListModel,
  allowAllRoles?: boolean,
) => {
  if (allowAllRoles || user?.userType === UserTypeEnum.Admin) {
    return data;
  }

  if (user?.userType === UserTypeEnum.StaffMember) {
    const staffMember = user as StaffMemberModel;
    if (currentAccount) {
      const roleId = staffMember.accountSiteStaffMembers.find(
        x => x.accountId === currentAccount.id,
      )?.staffRole.id;
      return data?.filter(x => x.id <= roleId);
    }

    const maxRole = _(staffMember.accountSiteStaffMembers)
      .map(x => x.staffRole.id)
      .max();
    return data?.filter(x => x.id <= maxRole);
  }

  return [];
};

export const eventReserved = (event: CalendarEvent) => {
  return !event.accountCategories?.length && event.accountRepresentatives?.length === 1;
};
